import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface DataFeedConfirmDialogData {
  title: string;
  message: string;
  confirmText: string;
  /** Material icon shown next to the title. */
  icon?: string;
  /** Warn styling for destructive actions (e.g. cancelling an import for good). */
  warn?: boolean;
  /** Points listed under the message (what the action does and does not do). */
  details?: string[];
  /** When set, a checkbox with this text must be ticked before the confirm button is enabled. */
  acknowledge?: string;
  /** A safer alternative offered next to the confirm button; the dialog closes with its value. */
  alternative?: { text: string; value: string };
}

/**
 * Confirm dialog for the Data Feed Monitor's Pause / Start / Stop actions. Closes with true when confirmed, with the
 * alternative's value when that was chosen, and with nothing when dismissed.
 */
@Component({
  selector: 'app-data-feed-confirm-dialog',
  template: `
    <h2 mat-dialog-title class="df-confirm-title" [class.warn]="data.warn">
      <mat-icon *ngIf="data.icon" aria-hidden="true">{{ data.icon }}</mat-icon>
      <span>{{ data.title }}</span>
    </h2>
    <mat-dialog-content class="df-confirm-body">
      <p class="df-confirm-message">{{ data.message }}</p>
      <ul *ngIf="data.details?.length" class="df-confirm-details" [class.warn]="data.warn">
        <li *ngFor="let detail of data.details">{{ detail }}</li>
      </ul>
      <mat-checkbox *ngIf="data.acknowledge" class="df-confirm-ack" color="warn" [checked]="acknowledged"
                    (change)="acknowledged = $event.checked">
        {{ data.acknowledge }}
      </mat-checkbox>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="df-confirm-actions">
      <button mat-button type="button" mat-dialog-close cdkFocusInitial>Cancel</button>
      <button *ngIf="data.alternative" mat-stroked-button type="button" color="primary" [mat-dialog-close]="data.alternative.value">
        {{ data.alternative.text }}
      </button>
      <button mat-flat-button type="button" [color]="data.warn ? 'warn' : 'primary'" [mat-dialog-close]="true"
              [disabled]="!!data.acknowledge && !acknowledged">
        {{ data.confirmText }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .df-confirm-title {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 24px;
    }
    .df-confirm-title::before { display: none; }
    .df-confirm-title .mat-icon { color: #142893; flex: 0 0 auto; }
    .df-confirm-title.warn .mat-icon { color: #b71c1c; }
    .df-confirm-message { margin: 0 0 8px; font-size: 14px; line-height: 20px; color: #161616; }
    .df-confirm-details { margin: 0 0 12px; padding-left: 20px; font-size: 13px; line-height: 20px; color: #393939; }
    .df-confirm-details li + li { margin-top: 4px; }
    .df-confirm-details.warn { border-left: 3px solid #b71c1c; padding: 8px 8px 8px 28px; background: #fff1f1; border-radius: 4px; }
    .df-confirm-ack { display: block; font-size: 14px; }
    .df-confirm-actions { gap: 8px; flex-wrap: wrap; }
  `],
})
export class DataFeedConfirmDialogComponent {
  acknowledged = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DataFeedConfirmDialogData) { }
}
